/**
 * Product-level Help copy shared by Web and Desktop.
 *
 * Keep only behavior that is truly common here. Browser capture, native
 * Desktop audio, OS permissions, updater/privacy behavior, and platform
 * navigation stay in the platform help-content modules.
 */
export const AI_PREFERENCE_ARTICLES = [
  {
    id: 'ai-vs-meeting-defaults',
    question: 'AI Personalization vs Meeting Defaults',
    answer:
      'AI Personalization (with Additional AI Settings) controls how KrishvaQ responds. Meeting Defaults control how new meetings start and which meeting behaviors are enabled by default. They are separate settings, and Help & Support itself is not stored as a preference.',
  },
  {
    id: 'answer-style',
    question: 'Answer Style',
    answer:
      'Answer Style changes how KrishvaQ explains an answer. Simple & Clear gets to the point, Balanced adds useful context, and Step-by-Step gives a deeper structured explanation. It is one of the AI Answer Settings, which apply only to meetings without an AI Response Style; meetings with one follow that style instead.',
  },
  {
    id: 'answer-length',
    question: 'Answer Length',
    answer:
      'Answer Length is the response length setting for AI answers: use Short for short answers, Normal for the standard amount of context, or Long for more detail. It does not change transcription length. Like the other AI Answer Settings, it applies only to meetings without an AI Response Style.',
  },
  {
    id: 'ai-language-tone',
    question: 'AI Language and Tone',
    answer:
      'Additional AI Settings > Language can be Same as Meeting, English, Hindi, or Gujarati, but the current live-meeting AI path does not read this account field; use the meeting\'s AI Response Language for live answer language. Tone can be Professional, Friendly, or Technical and is applied to live AI responses in meetings without an AI Response Style.',
  },
  {
    id: 'meeting-type-speaking-style',
    question: 'Meeting Type and Speaking Style',
    answer:
      'Meeting Type can be Interview, Client Meeting, Sales Call, or Normal Meeting and gives KrishvaQ context about the conversation in meetings started as "Meeting", including meetings with an AI Response Style. Speaking Style can be Professional or Natural and changes how an answer is phrased for spoken conversation in meetings without an AI Response Style.',
  },
  {
    id: 'live-ai-toggles',
    question: 'Auto Detect Questions and Show Talking Points',
    answer:
      'Auto Detect Questions controls whether the live session automatically detects participant questions and triggers the AI answer path. Show Talking Points controls whether the live UI displays follow-up talking-point suggestions.',
  },
  {
    id: 'ai-preference-save-behavior',
    question: 'When do Additional AI Settings changes take effect?',
    answer:
      'Use Save Changes to store Additional AI Settings. Answer Style, Answer Length, Tone, Meeting Type, and Speaking Style are read when a live meeting session connects, so an already-open session keeps its current values until a new or reconnected session starts.',
  },
];

export const MEETING_DEFAULT_ARTICLES = [
  {
    id: 'meeting-defaults-overview',
    question: 'What do Meeting Defaults change?',
    answer:
      'Meeting Defaults are saved separately from AI Personalization and Additional AI Settings. Language defaults and Default KrishvaQ mode are applied when a new meeting form opens, while Save Transcript is captured for each new meeting when it is created.',
  },
  {
    id: 'default-copilot-mode',
    question: 'Default KrishvaQ mode',
    answer:
      'Default KrishvaQ mode selects the initial mode on the New Meeting screen. You can still choose a different mode for that meeting before starting it.',
  },
  {
    id: 'save-transcript',
    question: 'Save Transcript',
    answer:
      'Save Transcript controls post-meeting retention of the raw transcript. Turning it off does not stop live transcription, translation, summaries, action items, or meeting memory; after post-meeting processing finishes, the raw transcript text is removed from meeting history for meetings created with this setting off.',
  },
  {
    id: 'auto-start-recording',
    question: 'Auto Start Recording',
    answer:
      'This switch is currently saved in Meeting Defaults, but the current Web and Desktop meeting-start flows do not read it. Start the meeting/capture normally rather than relying on this switch to begin recording automatically.',
  },
  {
    id: 'generate-summary-default',
    question: 'Generate Summary After Meeting',
    answer:
      'This switch is currently saved in Meeting Defaults, but the current post-meeting processing path does not read it and still runs meeting analysis. Do not rely on this switch to enable or suppress summary generation yet.',
  },
  {
    id: 'detect-speaker-names',
    question: 'Detect Speaker Names',
    answer:
      'This switch is currently saved in Meeting Defaults, but the active transcription pipeline does not read it. Speaker naming is therefore not controlled by this setting yet.',
  },
];

export const LANGUAGE_ARTICLES = [
  {
    id: 'language-differences',
    question: 'Spoken Language, Translate To, AI Response Language, and Preferred Language',
    answer:
      'Spoken Language tells speech recognition what is being spoken. Translate To controls translated transcript output, AI Response Language controls generated AI answers, and Preferred Language belongs to your account profile. These settings are related but are not interchangeable.',
  },
  {
    id: 'auto-multilingual',
    question: 'Auto / Multilingual spoken language',
    answer:
      'Auto / Multilingual is shown only when the active speech provider reports that it supports automatic language handling. The available language lists come from the current backend capabilities, so they can differ between deployments.',
  },
  {
    id: 'language-defaults',
    question: 'When do Language defaults apply?',
    answer:
      'Settings > Language stores defaults for new meetings. You can override the spoken, translation, and AI-response languages for a particular meeting without changing your profile Preferred Language.',
  },
  {
    id: 'ai-preference-language-vs-meeting',
    question: 'Additional AI Settings Language vs AI Response Language',
    answer:
      'AI Response Language on the meeting is authoritative for live answers, including the Same as Translation option. The account-level Additional AI Settings > Language field is currently saved but does not override the live meeting language, so use AI Response Language when you need to control answer language today.',
  },
];

export const DOCUMENT_BASE_ARTICLES = [
  {
    id: 'upload-documents',
    question: 'Uploading and creating documents',
    answer:
      'Open Documents to upload a file or create a manual text document. The backend currently accepts PDF, DOCX, PPTX, XLSX, CSV, and TXT uploads.',
  },
  {
    id: 'document-status',
    question: 'Document processing: Queued, Processing, Ready, and Failed',
    answer:
      'Queued means processing has not started, Processing means KrishvaQ is still extracting content, Ready means the document can be used, and Failed means processing did not complete. The Documents view refreshes while work is still pending.',
  },
  {
    id: 'delete-documents',
    question: 'Deleting a document',
    answer:
      'Use the delete action in Documents and confirm the request. Deleting removes the document from your library and from future use as meeting context.',
  },
  {
    id: 'documents-and-ai',
    question: 'How does KrishvaQ use documents?',
    answer:
      'Documents can be attached as context for a meeting so live AI answers can use relevant extracted content. Ask KrishvaQ currently searches saved meeting memory, not the general document library.',
  },
];

export const ASK_COPILOT_ARTICLES = [
  {
    id: 'ask-sources',
    question: 'What does Ask KrishvaQ search?',
    answer:
      'Ask KrishvaQ searches your saved meeting memory. That memory is built from completed meeting transcripts and available summaries, decisions, and action items, then used to answer questions across previous meetings.',
  },
  {
    id: 'ask-vs-live',
    question: 'Ask KrishvaQ vs Live AI Assistant',
    answer:
      'Live AI Assistant helps during the active meeting and can use that meeting\'s configured context. Ask KrishvaQ is for questions across saved meeting history after the fact.',
  },
  {
    id: 'ask-unavailable',
    question: 'Why is meeting memory unavailable?',
    answer:
      'Ask KrishvaQ depends on the meeting-memory search and AI services. If those services are temporarily unavailable, retry after the service recovers rather than treating the error as missing meeting data.',
  },
];

export const ANALYTICS_ARTICLES = [
  {
    id: 'analytics-overview',
    question: 'What does Analytics show?',
    answer:
      'Analytics summarizes saved meeting activity using the metrics currently available to your account, including meeting counts, total and average duration, translated meetings, AI question activity, language mix, and highlights.',
  },
  {
    id: 'analytics-range',
    question: 'Changing the Analytics time range',
    answer:
      'Use the activity range control to change the period shown by Analytics. The summary metrics and activity view update for the selected range; Analytics reports saved activity and does not change meeting or AI preferences.',
  },
];

export const NOTIFICATION_ARTICLES = [
  {
    id: 'notification-badge',
    question: 'Unread badge and opening notifications',
    answer:
      'The bell badge shows the unread count. Open the bell to load recent notifications; opening it refreshes the list, and a lightweight background refresh keeps the badge current.',
  },
  {
    id: 'notification-actions',
    question: 'Reading, opening, and deleting notifications',
    answer:
      'Select a notification to mark it read and, when it has a destination, open the related meeting, Documents, Plan & Billing, or Settings. You can also Mark all as read and dismiss individual notifications.',
  },
  {
    id: 'notification-types',
    question: 'What can KrishvaQ notify me about?',
    answer:
      'Current user-facing notifications include meeting completion or analysis failure, meetings left active, document processing success/failure, security events, subscription changes or cancellation, payment failures, and completed refunds. Some administrative billing notifications are visible only to administrators.',
  },
  {
    id: 'notification-troubleshooting',
    question: 'My notification list is not updating',
    answer:
      'Close and reopen the notification bell to force a fresh first page. A temporary API/network failure can leave the last successfully loaded list in place until the next refresh succeeds.',
  },
  {
    id: 'notification-badge-mismatch',
    question: 'I received a badge but do not see the notification',
    answer:
      'Open the bell to refresh the latest notification page. If an item was already read or dismissed in another active session, the unread badge and visible list can briefly differ until the next refresh.',
  },
  {
    id: 'security-notification',
    question: 'What does a security notification mean?',
    answer:
      'A security notification reports an account or device security event, such as a new device sign-in or a previously revoked device signing in again. If the activity is unfamiliar, change your password if your account uses one, revoke affected Desktop devices where available, and use Log Out All Devices if needed.',
  },
  {
    id: 'document-notification',
    question: 'Why did I receive a document notification?',
    answer:
      'Document notifications report that document processing completed successfully or failed. Open Documents to see the current Ready/Failed status and decide whether to use, delete, or upload the document again.',
  },
];

export const ACCOUNT_COMMON_ARTICLES = [
  {
    id: 'profile',
    question: 'Profile name, photo, language, and email',
    answer:
      'Settings > Profile lets you change your full name, profile photo, and Preferred Language. Your signed-in email is displayed but is not editable from the current profile editor.',
  },
  {
    id: 'appearance',
    question: 'Light, Dark, and System appearance',
    answer:
      'Settings > Appearance lets you choose Light, Dark, or System. System follows the appearance preference reported by the current operating system or browser environment.',
  },
];

export const BILLING_BASE_ARTICLES = [
  {
    id: 'billing-overview',
    question: 'Current plan, status, renewal/end date, and usage',
    answer:
      'Open Plan & Billing to see your current plan and status, the relevant renewal or end date, and usage for the current period. Use this authenticated billing area to manage an existing subscription.',
  },
  {
    id: 'payment-history',
    question: 'Payment and refund history',
    answer:
      'Plan & Billing shows payment history when billing is available. Payment rows can reflect outcomes such as paid, failed, or refunded, and completed refunds can also produce a notification.',
  },
  {
    id: 'cancel-subscription',
    question: 'Cancelling a subscription',
    answer:
      'Use Cancel subscription from Plan & Billing when the action is available. Cancellation can be scheduled for the end of the paid period, so access can continue until the displayed end date.',
  },
  {
    id: 'payment-failure',
    question: 'Payment failed',
    answer:
      'A failed payment is reflected in billing state and can generate a high-priority notification. Follow Plan & Billing for the current subscription state and any remaining access or grace-period information shown there.',
  },
];
